import { useEffect, useRef, useState, useCallback } from "react";
import api from "./api";

/**
 * Polls /astrologers/{id}/availability/me every 5s and exposes:
 *  - status: "available" | "busy"
 *  - activeSession: {mode, eta_seconds} | null
 *  - queueSize, queueFull, maxQueueSize
 *  - myPosition, myStatus ("waiting" | "ready" | null), myEtaSeconds, myMode
 *  - displayEtaSeconds: ticks down 1s per real-second between polls so the
 *    countdown feels live (server poll cadence is 5s).
 *
 * Auth-mode: passes `user` from caller. When unauthenticated, uses the
 * public endpoint (no per-user fields).
 *
 * Returns helper actions: joinQueue(mode), leaveQueue(), markStarted().
 */
export function useAstrologerAvailability(astrologerId, user, opts = {}) {
  const enabled = !!astrologerId && opts.enabled !== false;
  const [data, setData] = useState(null);
  const [displayEta, setDisplayEta] = useState(null);
  const lastPollAtRef = useRef(0);

  const fetchNow = useCallback(async () => {
    if (!enabled) return;
    try {
      const url = user
        ? `/astrologers/${astrologerId}/availability/me`
        : `/astrologers/${astrologerId}/availability`;
      const { data: d } = await api.get(url);
      setData(d);
      // Reset the live ticker off the freshly-polled ETA.
      const eta = d.my_eta_seconds ?? d.active_session?.eta_seconds ?? null;
      setDisplayEta(eta);
      lastPollAtRef.current = Date.now();
    } catch { /* swallow */ }
  }, [astrologerId, user, enabled]);

  // 5s poll loop
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await fetchNow();
      if (!cancelled) setTimeout(tick, 5000);
    };
    tick();
    return () => { cancelled = true; };
  }, [enabled, fetchNow]);

  // 1s "live" countdown between polls
  useEffect(() => {
    if (displayEta == null) return;
    const id = setInterval(() => {
      setDisplayEta((cur) => (cur == null ? cur : Math.max(0, cur - 1)));
    }, 1000);
    return () => clearInterval(id);
  }, [displayEta != null]); // eslint-disable-line react-hooks/exhaustive-deps

  const joinQueue = useCallback(async (mode) => {
    const { data: d } = await api.post(`/astrologers/${astrologerId}/queue/join`, { mode });
    setData(d);
    const eta = d.my_eta_seconds ?? d.active_session?.eta_seconds ?? null;
    setDisplayEta(eta);
    return d;
  }, [astrologerId]);

  const leaveQueue = useCallback(async () => {
    await api.post(`/astrologers/${astrologerId}/queue/leave`);
    await fetchNow();
  }, [astrologerId, fetchNow]);

  const markStarted = useCallback(async () => {
    try { await api.post(`/astrologers/${astrologerId}/queue/mark-started`); }
    catch { /* non-fatal */ }
  }, [astrologerId]);

  return {
    status: data?.status,
    isBusy: data?.status === "busy",
    activeSession: data?.active_session ?? null,
    queueSize: data?.queue_size ?? 0,
    queueFull: !!data?.queue_full,
    maxQueueSize: data?.max_queue_size ?? 3,
    myPosition: data?.my_position ?? null,
    myStatus: data?.my_status ?? null,
    myEtaSeconds: displayEta,
    myMode: data?.my_mode ?? null,
    refresh: fetchNow,
    joinQueue,
    leaveQueue,
    markStarted,
  };
}

/** Format seconds as compact "5m 22s" / "32s" / "1m". */
export function formatEta(secs) {
  if (secs == null) return "—";
  const s = Math.max(0, Math.floor(secs));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}
